import { useCallback, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { Box, Button, Divider, Heading, Modal, Toast, useToast } from '@zillow/constellation-10';
import { Landing } from '../components/LeaseRenewal/Tenant/Landing';
import {
  PROPOSED_RENT_AMOUNT_TYPE,
  WhatDoYouWantToIncludeInYourRenewal,
} from '../components/LeaseRenewal/Tenant/WhatDoYouWantToIncludeInYourRenewal';
import { HowLongDoYouWantToRenewYourLease } from '../components/LeaseRenewal/Tenant/HowLongDoYouWantToRenewYourLease';
import { ShareSomeDetailsAboutYourTenancy } from '../components/LeaseRenewal/Tenant/ShareSomeDetailsAboutYourTenancy';
import { ReviewOrEditYourMessage } from '../components/LeaseRenewal/Tenant/ReviewOrEditYourMessage';
import {
  useLeaseRenewalPreferenceWebApiHandler_createPreference,
  useLeaseRenewalPreferenceWebApiHandler_createWithPreferences,
  useLeaseRenewalPreferenceWebApiHandler_sendRenewalProposal,
  useLeaseRenewalWebApiHandler_canTenantAskForRenewal,
  useLeaseWebApiHandler_getLease,
} from '@zg-rentals/ts-leasing-client-react-query';
import { AdditionalRequest, LeaseRenewalTermPreference, TenantTrait } from '@zg-rentals/ts-leasing-client';
import { useLocation } from 'react-router';
import { getUTCTimeFromDateString } from '../utils/dates';
import { useLeasingUrlParams } from '../hooks/useLeasingUrlParams';
import { logError } from '@zg-rentals/log-error';
import ExitModal from '../components/Widgets/ExitModal';
import { useRecoilState } from 'recoil';
import { exitModalState, renewalProposalMessageState } from '../../atoms';
import { getRenterInboxByConversationIdPath } from '../utils/urlUtils';
import { ModalBodyFullHeightLoadingMask } from '../constants/styleConstants';
import { useIsFetching, useIsMutating } from '@zg-rentals/react-query5';
import { addYears } from 'date-fns';
import { getDefaultMessageText } from '../utils/formatters';
import { Field } from '../models';

enum STEP_IDS {
  Landing = 'Landing',
  WhatDoYouWantToIncludeInYourRenewal = 'WhatDoYouWantToIncludeInYourRenewal',
  HowLongDoYouWantToRenewYourLease = 'HowLongDoYouWantToRenewYourLease',
  ShareSomeDetailsAboutYourTenancy = 'ShareSomeDetailsAboutYourTenancy',
  ReviewOrEditYourMessage = 'ReviewOrEditYourMessage',
}

const WIZARD_STEPS = [
  {
    id: STEP_IDS.Landing,
    component: <Landing />,
  },
  {
    id: STEP_IDS.WhatDoYouWantToIncludeInYourRenewal,
    component: <WhatDoYouWantToIncludeInYourRenewal />,
  },
  {
    id: STEP_IDS.HowLongDoYouWantToRenewYourLease,
    component: <HowLongDoYouWantToRenewYourLease />,
  },
  {
    id: STEP_IDS.ShareSomeDetailsAboutYourTenancy,
    component: <ShareSomeDetailsAboutYourTenancy />,
  },
  {
    id: STEP_IDS.ReviewOrEditYourMessage,
    component: <ReviewOrEditYourMessage />,
  },
];

export interface LeaseRenewalFormValues {
  [FIELD_IDS.RENT_TYPE]?: string | boolean;
  [FIELD_IDS.RENT_AMOUNT]?: string;
  [FIELD_IDS.WAIVE_LATE_FEES_CHECKED]?: boolean;
  [FIELD_IDS.WAIVE_LATE_FEES]?: string;
  [FIELD_IDS.UPGRADE_APPLIANCE_CHECKED]?: boolean;
  [FIELD_IDS.UPGRADE_APPLIANCE]?: string;
  [FIELD_IDS.PROFESSIONAL_CLEANING_CHECKED]?: boolean;
  [FIELD_IDS.YARD_MAINTENANCE_CHECKED]?: boolean;
  [FIELD_IDS.YARD_MAINTENANCE]?: string;
  [FIELD_IDS.RENEWAL_TYPE]?: LeaseRenewalTermPreference;
  [FIELD_IDS.RENEWAL_END_DATE]?: string;
  [FIELD_IDS.PAY_RENT_ON_TIME_CHECKED]?: boolean;
  [FIELD_IDS.TAKES_CARE_OF_PROPERTY_CHECKED]?: boolean;
  [FIELD_IDS.RARELY_REQUESTS_REPAIRS_CHECKED]?: boolean;
  [FIELD_IDS.NO_NOISE_COMPLAINTS_CHECKED]?: boolean;
  [FIELD_IDS.RENEWAL_PROPOSAL_MESSAGE]?: string;
}

type StepValidationFunction = (values?: LeaseRenewalFormValues) => boolean;

const stepValidations: Record<STEP_IDS, StepValidationFunction> = {
  [STEP_IDS.Landing]: () => true,
  [STEP_IDS.WhatDoYouWantToIncludeInYourRenewal]: (values: LeaseRenewalFormValues = {}) => {
    if (!values[FIELD_IDS.RENT_AMOUNT]) return false;

    const conditionalValidations = [
      values[FIELD_IDS.WAIVE_LATE_FEES_CHECKED] && !values[FIELD_IDS.WAIVE_LATE_FEES],
      values[FIELD_IDS.UPGRADE_APPLIANCE_CHECKED] && !values[FIELD_IDS.UPGRADE_APPLIANCE],
      values[FIELD_IDS.YARD_MAINTENANCE_CHECKED] && !values[FIELD_IDS.YARD_MAINTENANCE],
    ];

    return !conditionalValidations.some(Boolean);
  },
  [STEP_IDS.HowLongDoYouWantToRenewYourLease]: (values: LeaseRenewalFormValues = {}) => {
    if (values[FIELD_IDS.RENEWAL_TYPE] === LeaseRenewalTermPreference.CUSTOM) {
      return !!values[FIELD_IDS.RENEWAL_END_DATE];
    }

    return !!values[FIELD_IDS.RENEWAL_TYPE];
  },
  [STEP_IDS.ShareSomeDetailsAboutYourTenancy]: () => true,
  [STEP_IDS.ReviewOrEditYourMessage]: (values: LeaseRenewalFormValues = {}) => {
    return !!values[FIELD_IDS.RENEWAL_PROPOSAL_MESSAGE];
  },
};

const useStepValidation = (currentStep: STEP_IDS): boolean => {
  const { watch, formState } = useFormContext<LeaseRenewalFormValues>();
  const values = watch();
  const { errors } = formState;

  if (currentStep === STEP_IDS.WhatDoYouWantToIncludeInYourRenewal && errors[FIELD_IDS.RENT_AMOUNT]) {
    return false;
  }

  if (currentStep === STEP_IDS.HowLongDoYouWantToRenewYourLease && errors[FIELD_IDS.RENEWAL_END_DATE]) {
    return false;
  }

  return stepValidations[currentStep]?.(values) ?? false;
};

export enum FIELD_IDS {
  RENT_TYPE = 'rent-type',
  RENT_AMOUNT = 'rent-amount',
  WAIVE_LATE_FEES_CHECKED = 'waive-late-fees-checked',
  WAIVE_LATE_FEES = 'waive-late-fees',
  UPGRADE_APPLIANCE_CHECKED = 'upgrade-appliance-checked',
  UPGRADE_APPLIANCE = 'upgrade-appliance',
  PROFESSIONAL_CLEANING_CHECKED = 'professional-cleaning-checked',
  YARD_MAINTENANCE_CHECKED = 'yard-maintenance-checked',
  YARD_MAINTENANCE = 'yard-maintenance',
  IS_CUSTOM_RENEWAL_LENGTH = 'is-custom-renewal-length',
  RENEWAL_TYPE = 'renewal-type',
  RENEWAL_END_DATE = 'renewal-end-date',
  PAY_RENT_ON_TIME_CHECKED = 'pay-rent-on-time-checked',
  TAKES_CARE_OF_PROPERTY_CHECKED = 'takes-care-of-property-checked',
  RARELY_REQUESTS_REPAIRS_CHECKED = 'rarely-request-repairs-checked',
  NO_NOISE_COMPLAINTS_CHECKED = 'no-noise-complaints-checked',
  RENEWAL_PROPOSAL_MESSAGE = 'renewal-proposal-message',
}

function TenantDrivenLeaseRenewalContent() {
  const [, setIsExitModalOpen] = useRecoilState(exitModalState);
  const [proposalMessage, setProposalMessage] = useRecoilState(renewalProposalMessageState);
  const location = useLocation();
  const { leaseId } = useLeasingUrlParams();
  const searchParams = new URLSearchParams(location.search);
  const conversationId = searchParams.get('conversationId');
  const satelliteConversationPath = getRenterInboxByConversationIdPath(conversationId);
  const { getValues, setValue } = useFormContext<LeaseRenewalFormValues>();
  const formValues = getValues();
  const { enqueueToast } = useToast();
  const onSubmitLeaseRenewalProposalErrorToast = useCallback(() => {
    enqueueToast(<Toast tone="critical">Unable to submit lease renewal proposal</Toast>);
  }, [enqueueToast]);
  const { data: leaseData } = useLeaseWebApiHandler_getLease({ leaseId });
  const { data, isLoading: isCanTenantAskForRenewalLoading } = useLeaseRenewalWebApiHandler_canTenantAskForRenewal({
    leaseId,
  });
  const { mutateAsync: createLeaseRenewalPreferences } = useLeaseRenewalPreferenceWebApiHandler_createPreference();
  const { mutateAsync: sendLeaseRenewalProposalMessage } = useLeaseRenewalPreferenceWebApiHandler_sendRenewalProposal();
  const { mutateAsync: createLeaseRenewalWithPreferences } =
    useLeaseRenewalPreferenceWebApiHandler_createWithPreferences();

  const canTenantAskForRenewal = data?.canTenantAskForLeaseRenewal;

  const isFetching = useIsFetching();
  const isMutating = useIsMutating();
  const isWizardLoading = isFetching || isMutating;

  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const currentStep = WIZARD_STEPS[currentStepIndex];
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === WIZARD_STEPS.length - 1;
  const isNextStepReviewOrEditYourMessage =
    WIZARD_STEPS[currentStepIndex + 1]?.id === STEP_IDS.ReviewOrEditYourMessage || false;

  const isCurrentStepValid = useStepValidation(currentStep.id);

  const handleNext = async () => {
    if (isLastStep) {
      const postData = {
        additionalRequests: [
          formValues[FIELD_IDS.WAIVE_LATE_FEES_CHECKED] &&
            formValues[FIELD_IDS.WAIVE_LATE_FEES] && {
              additionalRequest: AdditionalRequest.WAIVE_LATE_FEE,
              additionalRequestValue: formValues[FIELD_IDS.WAIVE_LATE_FEES],
            },
          formValues[FIELD_IDS.UPGRADE_APPLIANCE_CHECKED] &&
            formValues[FIELD_IDS.UPGRADE_APPLIANCE] && {
              additionalRequest: AdditionalRequest.UPGRADE_APPLIANCE,
              additionalRequestValue: formValues[FIELD_IDS.UPGRADE_APPLIANCE],
            },
          formValues[FIELD_IDS.PROFESSIONAL_CLEANING_CHECKED] && {
            additionalRequest: AdditionalRequest.PROFESSIONAL_CLEANING,
            additionalRequestValue: null,
          },
          formValues[FIELD_IDS.YARD_MAINTENANCE_CHECKED] &&
            formValues[FIELD_IDS.YARD_MAINTENANCE] && {
              additionalRequest: AdditionalRequest.YARD_MAINTENANCE,
              additionalRequestValue: formValues[FIELD_IDS.YARD_MAINTENANCE],
            },
          formValues[FIELD_IDS.RENT_TYPE] === PROPOSED_RENT_AMOUNT_TYPE.FIRST_MONTH_15_PERCENT_OFF && {
            additionalRequest: AdditionalRequest.FIRST_MONTH_REDUCED_RENT_15,
            additionalRequestValue: null,
          },
        ].filter(Boolean),
        leaseId: Number(leaseId),
        proposedRentAmount: Field.getValueInCents(formValues[FIELD_IDS.RENT_AMOUNT]),
        renewalEndDate:
          formValues[FIELD_IDS.RENEWAL_TYPE] === LeaseRenewalTermPreference.CUSTOM
            ? Number(getUTCTimeFromDateString(formValues[FIELD_IDS.RENEWAL_END_DATE]))
            : formValues[FIELD_IDS.RENEWAL_TYPE] === LeaseRenewalTermPreference.ONE_YEAR
              ? leaseData?.lease?.endDate
                ? (() => {
                    const date = addYears(leaseData.lease.endDate, 1);
                    date.setUTCHours(0, 0, 0, 0);
                    return date.getTime();
                  })()
                : null
              : formValues[FIELD_IDS.RENEWAL_TYPE] === LeaseRenewalTermPreference.TWO_YEAR
                ? leaseData?.lease?.endDate
                  ? (() => {
                      const date = addYears(leaseData.lease.endDate, 2);
                      date.setUTCHours(0, 0, 0, 0);
                      return date.getTime();
                    })()
                  : null
                : null,
        renewalType: formValues[FIELD_IDS.RENEWAL_TYPE],
        tenantTraits: [
          formValues[FIELD_IDS.PAY_RENT_ON_TIME_CHECKED] && {
            tenantTrait: TenantTrait.PAYS_ON_TIME,
            tenantTraitValue: '',
          },
          formValues[FIELD_IDS.TAKES_CARE_OF_PROPERTY_CHECKED] && {
            tenantTrait: TenantTrait.TAKES_CARE_OF_PROPERTY,
            tenantTraitValue: '',
          },
          formValues[FIELD_IDS.RARELY_REQUESTS_REPAIRS_CHECKED] && {
            tenantTrait: TenantTrait.RARELY_REQUESTS_REPAIRS,
            tenantTraitValue: '',
          },
          formValues[FIELD_IDS.NO_NOISE_COMPLAINTS_CHECKED] && {
            tenantTrait: TenantTrait.NO_COMPLAINTS,
            tenantTraitValue: '',
          },
        ].filter(Boolean),
      };

      try {
        const leaseRenewalPreferencesData = await createLeaseRenewalPreferences(
          { postData },
          {
            onError(error) {
              logError({
                error,
                context: {
                  details: 'TenantDrivenLeaseRenewal - createWithPreferences',
                  leaseId,
                },
              });
            },
          },
        );

        const renewalProposalMessage = getValues(FIELD_IDS.RENEWAL_PROPOSAL_MESSAGE);
        if (!renewalProposalMessage) {
          throw new Error(`Invalid lease renewal proposal message: typeof ${typeof renewalProposalMessage}`);
        }
        await sendLeaseRenewalProposalMessage(
          {
            postData: {
              leaseId: Number(leaseId),
              proposalTextBody: renewalProposalMessage,
            },
          },
          {
            onError(error) {
              logError({
                error,
                context: {
                  details: 'TenantDrivenLeaseRenewal - sendLeaseRenewalProposalMessage',
                  leaseId,
                },
              });
            },
          },
        );

        await createLeaseRenewalWithPreferences(
          {
            postData: {
              leaseId: Number(leaseId),
              leaseRenewalPreferenceId: leaseRenewalPreferencesData.leaseRenewalPreferenceId,
            },
          },
          {
            onSuccess() {
              window.location.href = satelliteConversationPath;
            },
            onError(error) {
              logError({
                error,
                context: {
                  details: 'TenantDrivenLeaseRenewal - createWithPreferences',
                  leaseId,
                },
              });
            },
          },
        );
      } catch (error) {
        logError({
          error,
          context: {
            details: 'TenantDrivenLeaseRenewal - submitLeaseRenewalProposal',
            leaseId,
          },
        });
        onSubmitLeaseRenewalProposalErrorToast();
      }
    } else {
      if (
        (isNextStepReviewOrEditYourMessage && proposalMessage === formValues[FIELD_IDS.RENEWAL_PROPOSAL_MESSAGE]) ||
        !formValues[FIELD_IDS.RENEWAL_PROPOSAL_MESSAGE]
      ) {
        const landlordFirstName = leaseData?.lease?.landlords[0].firstName || '';
        const tenantFirstName = leaseData?.lease.tenants[0].firstName || '';
        const defaultMessageText = getDefaultMessageText({
          leaseRenewalFormData: formValues,
          leaseInfo: { landlordFirstName, tenantFirstName },
        });
        setValue(FIELD_IDS.RENEWAL_PROPOSAL_MESSAGE, defaultMessageText);
        setProposalMessage(defaultMessageText);
      }

      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  const handleBack = () => {
    if (!isFirstStep) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  if (canTenantAskForRenewal === false) {
    window.location.href = satelliteConversationPath;
  }

  return (
    <>
      <Modal
        open
        size="fullScreen"
        onOpenChange={() => {}}
        header={
          <>
            <Box
              css={{
                display: 'flex',
                marginTop: 'tight',
                marginBottom: 'tight',
                marginLeft: { base: 'tight', md: 'loosest' },
                marginRight: { base: 'tight', md: 'loosest' },
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box
                css={{
                  display: 'flex',
                  alignItems: { base: 'flex-start', md: 'center' },
                  flexDirection: { base: 'column', md: 'row' },
                }}
              >
                <Heading level={2} css={{ textStyle: 'responsive.heading-sm', marginRight: 'tight' }}>
                  Start a lease renewal
                </Heading>
              </Box>
              <Button tone="neutral" emphasis="outlined" onClick={() => setIsExitModalOpen(true)}>
                Exit
              </Button>
            </Box>
            <Divider />
          </>
        }
        body={
          <ModalBodyFullHeightLoadingMask loading={isWizardLoading}>
            <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(24, 1fr)' }}>
              <Box css={{ gridColumnEnd: { base: 'span 0', lg: 'span 3' } }} />
              <Box css={{ gridColumnEnd: { base: 'span 24', lg: 'span 18' } }}>
                <Box css={{ marginTop: { base: 'none', sm: 'looser' } }} />
                {currentStep.component}
              </Box>
              <Box css={{ gridColumnEnd: { base: 'span 0', lg: 'span 3' } }} />
            </Box>
          </ModalBodyFullHeightLoadingMask>
        }
        footer={
          <>
            <Divider />
            <Box
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 'tight',
                marginBottom: 'tight',
                marginLeft: { base: 'tight', md: 'loosest' },
                marginRight: { base: 'tight', md: 'loosest' },
              }}
            >
              {!isFirstStep && (
                <Button tone="brand" emphasis="outlined" onClick={handleBack} disabled={!!isWizardLoading}>
                  Back
                </Button>
              )}
              <div />
              {!isCanTenantAskForRenewalLoading && (
                <Button
                  tone="brand"
                  emphasis="filled"
                  onClick={handleNext}
                  disabled={!!isWizardLoading || !isCurrentStepValid}
                >
                  {isLastStep ? 'Submit' : currentStep.id === STEP_IDS.Landing ? 'Get started' : 'Next'}
                </Button>
              )}
            </Box>
          </>
        }
      />
      <ExitModal path={satelliteConversationPath} />
    </>
  );
}

export function TenantDrivenLeaseRenewal() {
  const form = useForm({ mode: 'onChange' });

  return (
    <FormProvider {...form}>
      <TenantDrivenLeaseRenewalContent />
    </FormProvider>
  );
}
